import * as React from 'react';
import { memo } from 'react';

import { keyBy } from 'lodash';

import { Autocomplete, Grid, TextField } from '@emburse/ui-core';

import { IExpenseTagBucketModel, IExpenseTagModel } from '@Shared/models';

interface IExpenseTagBucketsFormControlsProps {
  multiple?: boolean; // Whether or not a dropdown expense tag bucket should support multiple selections
  xs?: number; // Will be passed as the xs prop for the <Grid> this component renders
  expenseTagsBucketsForUser: IExpenseTagBucketModel[];
  activeExpenseBucketsWithActiveTags: IExpenseTagBucketModel[];
  selectedExpenseTagsByBucketId?: Record<string, string | string[]>;
  textExpenseTagsByBucketId?: Record<string, string>;
  disabled?: boolean;
  onExpenseTagChange?: (bucketId: string, val: string | string[]) => void; // Will be called with val: string[] if and only if this is a dropdown bucket and multiple is set to true
}

const ExpenseTagBucketsFormControls: React.FC<IExpenseTagBucketsFormControlsProps> = ({
  multiple,
  xs = 6,
  expenseTagsBucketsForUser,
  activeExpenseBucketsWithActiveTags,
  selectedExpenseTagsByBucketId = {},
  textExpenseTagsByBucketId = {},
  disabled,
  onExpenseTagChange
}) => {
  const expenseTagBucketControls = expenseTagsBucketsForUser.map((bucketDetail) => {
    const bucketId = bucketDetail.id;
    let selectedValueOrValues: string | string[]; // Will be string for multiple=false, string[] for multiple=true
    const expenseTagBucketsWithExpenseTagsById = keyBy(activeExpenseBucketsWithActiveTags, 'id');
    if (bucketDetail.input_type === 'dropdown' && expenseTagBucketsWithExpenseTagsById[bucketId].expense_tags) {
      selectedValueOrValues = selectedExpenseTagsByBucketId[bucketId];
    }

    if (disabled) {
      // reset selected value
      selectedValueOrValues = multiple ? [] : '';
    }

    const renderDropDown = () => {
      const dropdownSelectedValue = multiple
        ? ((selectedValueOrValues as string[]) || []).map((selectedTagId) =>
            expenseTagBucketsWithExpenseTagsById[bucketId].expense_tags.find((tag) => tag.id === selectedTagId)
          )
        : expenseTagBucketsWithExpenseTagsById[bucketId].expense_tags.find((tag) => tag.id === selectedValueOrValues);

      const multiAutocompleteProps = multiple ? { multiple, checkbox: true, multipleType: 'text' } : {};

      return (
        // @ts-ignore
        <Autocomplete
          key={Boolean(dropdownSelectedValue) as any}
          {...multiAutocompleteProps}
          useVirtualization
          label={bucketDetail.name}
          value={dropdownSelectedValue}
          defaultValue={dropdownSelectedValue}
          options={expenseTagBucketsWithExpenseTagsById[bucketId].expense_tags}
          getOptionLabel={(opt: IExpenseTagModel) => opt.name}
          disabled={disabled}
          onChange={(_: React.ChangeEvent<HTMLSelectElement>, value: IExpenseTagModel | IExpenseTagModel[]) => {
            let idOrIds;
            if (!multiple) {
              idOrIds = value ? (value as IExpenseTagModel).id : '';
            } else {
              idOrIds = (value as IExpenseTagModel[]).map((val) => val.id);
            }

            onExpenseTagChange(bucketId, idOrIds);
          }}
        />
      );
    };

    return (
      <Grid item {...(typeof xs === 'number' ? { xs } : {})} key={bucketId}>
        {bucketDetail.input_type === 'text' && (
          <TextField
            fullWidth
            label={bucketDetail.name}
            value={disabled ? '' : textExpenseTagsByBucketId[bucketId] || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onExpenseTagChange(bucketId, event.target.value)}
            disabled={disabled}
          />
        )}
        {bucketDetail.input_type === 'dropdown' && renderDropDown()}
      </Grid>
    );
  });
  return <>{expenseTagBucketControls}</>;
};

const MemoizedExpenseTagBucketsFormControls = memo(ExpenseTagBucketsFormControls);

export { MemoizedExpenseTagBucketsFormControls as ExpenseTagBucketsFormControls };
